import { differenceInMinutes, format, parseISO, startOfToday } from "../../../date-fns/index.js";
const meridiemReg = "(?<meridiem>[AaPp])";
const separatorsReg = "[:.]";
const formats24 = {
  fourDigits: `(2[0-4]|[0-1][0-9])${separatorsReg}?([0-5]?[0-9])`,
  threeDigits: `([0-9]${separatorsReg}?[0-5][0-9])`,
  lessThanThreeDigits: `([0-9]${separatorsReg}[0-9])|(2[0-4]|[0-1][0-9]|([0-9]$))`
};
const formats12 = {
  fourDigits: `(0[0-9]|1[0-2])${separatorsReg}[0-5]?[0-9])`,
  threeDigits: `([0-9]${separatorsReg}?[0-5][0-9])`,
  lessThanThreeDigits: `(1[0-2]|0[0-9]|[0-9])`
};
const regEx24 = `^(?<time24>${formats24.fourDigits}|${formats24.threeDigits}|${formats24.lessThanThreeDigits})`;
const regEx12 = `^(?<time12>(${formats12.fourDigits}|${formats12.threeDigits}|${formats12.lessThanThreeDigits}) ?${meridiemReg}`;
const regEx = new RegExp(`${regEx12}|${regEx24}`);
const convertTimeInputCache = /* @__PURE__ */ new Map();
function convertTimeInput(input, defaultFormat, allowEmpty) {
  const timeInput = input.trim();
  const cacheId = `${input}${defaultFormat}`;
  if (convertTimeInputCache.has(cacheId)) {
    const cachedResult = convertTimeInputCache.get(cacheId);
    if (cachedResult) return cachedResult;
  }
  if (timeInput === "") {
    if (allowEmpty) return null;
    return "invalid";
  }
  const groups = timeInput.match(regEx)?.groups;
  if (!groups) return "invalid";
  let timeValue = groups.meridiem ? groups.time12 : groups.time24;
  const separator = timeValue.match(/[:.]/);
  if (separator) {
    const [hours, minutes] = timeValue.split(separator[0]);
    timeValue = `${hours.padStart(2, "0")}${minutes.padEnd(2, "0")}`;
  } else {
    switch (timeValue.length) {
      case 3:
        timeValue = `0${timeValue}`;
        break;
      case 2:
        timeValue = `${timeValue}00`;
        break;
      case 1:
        timeValue = `0${timeValue}00`;
        break;
    }
  }
  if (timeValue === "24" || timeValue === "2400") {
    timeValue = "0000";
  }
  const today = startOfToday();
  let hoursString = timeValue.substr(0, 2);
  switch (groups?.meridiem) {
    case "a":
    case "A":
      if (hoursString === "12") {
        hoursString = "00";
      }
      timeValue = `${hoursString}${timeValue.substr(2, 2)}`;
      break;
    case "p":
    case "P": {
      if (hoursString === "12") break;
      const hours = Number.parseInt(hoursString, 10);
      timeValue = `${hours + 12}${timeValue.substr(2, 2)}`;
      break;
    }
    /**
     * Let's fix ambiguous cases where a value without a meridiem could lead
     * to some unwanted conversions like 12:00
     * that in 24h format should be converted to 12:00 (12:00pm)
     * but in 12h format should be converted to 12:00am
     */
    default:
      if (defaultFormat === "hh:mm aa") {
        const hours = Number.parseInt(hoursString, 10);
        if (hours === 12) {
          timeValue = `00${timeValue.substr(2, 2)}`;
        }
      }
      break;
  }
  const toParse = `${format(today, "yyyy-MM-dd")}T${timeValue}`;
  const result = differenceInMinutes(parseISO(toParse), startOfToday());
  if (Number.isNaN(result)) {
    return "invalid";
  }
  convertTimeInputCache.set(cacheId, result);
  return result;
}
export {
  convertTimeInput
};
//# sourceMappingURL=convertTimeInput.js.map
